import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import { ProductQualityClient, ProductQualityResponse, ProductQualityRequest } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface ProductQuality extends ProductQualityResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    productQualityList: ProductQuality[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    productQualityList: [],
};

const productQualitySlice = createSlice({
    name: 'productQuality',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getProductQualityListSuccess: (state, action: PayloadAction<ProductQuality[]>) => {
            state.productQualityList = action.payload;
            state.isLoading = false;
        },
        addNewProductQualitySuccess: (state, action: PayloadAction<ProductQuality>) => {
            const newItem = action.payload;
            state.productQualityList = [newItem, ...state.productQualityList];
            state.isLoading = false;
        },
        updateProductQualitySuccess: (state, action: PayloadAction<ProductQuality>) => {
            const updatedItem = action.payload;
            const index = state.productQualityList.findIndex(item => item.productQualityId === updatedItem.productQualityId);
            state.productQualityList = [...state.productQualityList.slice(0, index), updatedItem, ...state.productQualityList.slice(index + 1)];
            state.isLoading = false;
        },
    },
});

// Reducer
export default productQualitySlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getProductQualityListSuccess,
    addNewProductQualitySuccess,
    updateProductQualitySuccess
} = productQualitySlice.actions;

// for useSelector
export const selectProductQuality = (state: RootState) => state.productQuality as State;

// ----------------------------------------------------------------------
export function getAllProductQuality(ignoreCache: boolean = false) {
    return async (dispatch, getState) => {
        try {
            const { productQualityList } = selectProductQuality(getState());
            if (ignoreCache || productQualityList.length === 0) {
                dispatch(productQualitySlice.actions.startLoading());
                const client = new ProductQualityClient(undefined, axios);
                const response = await client.getAll(false);
                dispatch(productQualitySlice.actions.getProductQualityListSuccess(response));
            }
        } catch (error) {
            dispatch(productQualitySlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createProductQuality(productQuality: ProductQuality, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(productQualitySlice.actions.startLoading());
        try {
            const { productQualityId, ...payload } = productQuality;
            const request = payload as ProductQualityRequest;
            const client = new ProductQualityClient(undefined, axios);
            const response = await client.create(request);
            dispatch(productQualitySlice.actions.addNewProductQualitySuccess(response));
            if (showNotification) {
                toast.success(`${response.productQualityCode} created successfully!`);
            }
        } catch (error) {
            dispatch(productQualitySlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateProductQuality(productQuality: ProductQuality, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(productQualitySlice.actions.startLoading());
        try {
            const { productQualityId, ...payload } = productQuality;
            const request = payload as ProductQualityRequest;
            const client = new ProductQualityClient(undefined, axios);
            const response = await client.edit(productQualityId, request);
            dispatch(productQualitySlice.actions.updateProductQualitySuccess(response));
            if (showNotification) {
                toast.success(`${response.productQualityCode} ${response.isActive ? 'updated' : 'deleted'} successfully!`);
            }
        } catch (error) {
            dispatch(productQualitySlice.actions.hasError(true));
            handleError(error);
        }
    };
}